import React, { useRef, useState, useEffect, useCallback } from 'react';
import { MessageCircle, Trash2, PenSquare, MoreVertical, Flag, UserMinus, Layers } from 'lucide-react';
import LNImage from '../ln-image';
import Carousel, { CarouselContextProps, CarouselProvider } from '@/components/common/carousel';
import ImagePagination from '@/components/complex/image-pagination';
import Link from 'next/link';
import { FeedDto, FeedButtonDto } from '@/libs/api/feed';
import FollowButton from '@/screen/account/components/follow-button';
import api from '@/libs/api';
import { useAccountFollow } from '@/libs/hooks/useAccountFollow';
import { useAuth } from '@/components/context/auth-context';
import { useModal } from '@/components/context/modal-context';
import Button from '@/components/primitives/button';
import { useRouter, usePathname } from 'next/navigation';
import { formatRelativeTime } from '@/libs/utils/date';
import Caption from '@/components/complex/caption';
import styles from './styles.module.scss';
import Typography from '@/components/primitives/typography';
import Avatar from '@/components/primitives/avatar';
import { useFeedLike } from '@/screen/feed/view/hooks/useFeedLike';
import LikeButton from '@/screen/feed/view/components/like-button';
import ShareButton from '@/screen/feed/view/components/share-button';
import { simplifyNumber } from '@/libs/utils/number';
import { DropDown, DropDownItem } from '@/components/primitives/dropdown';
import { useToast } from '@/components/context/toast-context';

interface Props extends FeedDto {
  onComment?: () => void;
  onShare?: () => void;
  onDelete?: (id: string, ownerName?: string) => void;
  disableNavigate?: boolean;
  /** 목록의 첫 번째 피드일 때 true로 전달하면 첫 이미지를 LCP 우선순위로 로드한다. */
  isFirst?: boolean;
  /**
   * list: 목록 카드. PC(769px 이상)에서는 masonry 갤러리 카드(사진 1:1 · 캡션 4줄 · 좋아요/댓글 알약)로 보인다.
   * detail: 피드 상세 본문. PC에서도 캡션 전문과 연관 장소·공유를 보여 준다.
   * 모바일은 두 경우 모두 기존 앱형 피드와 같다.
   */
  variant?: 'list' | 'detail';
}

// 서버와 첫 클라이언트 렌더가 같은 문자열을 내도록 타임존과 포맷을 고정한다.
const absoluteDateFormatter = new Intl.DateTimeFormat('ko-KR', {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  timeZone: 'Asia/Seoul',
});

// 서버 렌더에서는 UA를 알 수 없으므로 UNKNOWN에서 시작하고 mount 후에 판정한다.
type Platform = 'UNKNOWN' | 'IOS' | 'ANDROID' | 'PC';

// 해시태그(#tag)를 제거한 뒤 앞 80자를 이미지 alt로 사용한다.
const buildFeedImageAlt = (caption: string | undefined, ownerLabel: string) => {
  if (caption) {
    const withoutHashtags = caption.replace(/#[^\s#]+/gu, '').replace(/\s+/g, ' ').trim();
    if (withoutHashtags) {
      return withoutHashtags.slice(0, 80);
    }
  }
  return `${ownerLabel}의 피드 이미지`;
};

const FeedItem = (props: Props) => {
  const {
    sid = '',
    caption,
    owner,
    files,
    links,
    isLike = false,
    createdAt,
    likeCount = 0,
    commentCount = 0,
    shareCount = 0,
    onComment,
    onDelete,
    isFirst = false,
    variant = 'list',
  } = props;
  const detailHref = sid ? `/feeds/${sid}` : undefined;
  const canNavigate = !(props.disableNavigate ?? false) && !!detailHref;
  const ownerLabel = owner?.nickname || owner?.name || '사용자';
  const feedImageAlt = buildFeedImageAlt(caption, ownerLabel);
  // 커머스 접근 차단 중에는 장소 링크만 렌더링한다.
  const placeLinks = (links ?? []).filter((link) => link.place);
  const initialFollowing = owner?.isFollowing || false;
  const auth = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { handleLike } = useFeedLike(props);
  const { isFollowing, isLoading, handleFollow } = useAccountFollow(owner, initialFollowing);
  const toast = useToast();

  const [localIsLike, setLocalIsLike] = useState(isLike);
  const [localLikeCount, setLocalLikeCount] = useState(likeCount);
  const [localShareCount, setLocalShareCount] = useState(shareCount);
  // 서버 HTML과 첫 클라이언트 렌더를 일치시키기 위한 mount 이후 값들.
  const [platform, setPlatform] = useState<Platform>('UNKNOWN');
  const [relativeTime, setRelativeTime] = useState<string>();

  useEffect(() => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) setPlatform('IOS');
    else if (/android/i.test(ua)) setPlatform('ANDROID');
    else setPlatform('PC');
  }, []);

  // 상대 시간은 현재 시각에 의존하므로 ISR HTML과 어긋난다. mount 후에만 계산한다.
  useEffect(() => {
    setRelativeTime(createdAt ? formatRelativeTime(createdAt) : undefined);
  }, [createdAt]);

  useEffect(() => {
    setLocalIsLike(isLike);
  }, [isLike]);

  useEffect(() => {
    setLocalLikeCount(likeCount);
  }, [likeCount]);

  useEffect(() => {
    setLocalShareCount(shareCount);
  }, [shareCount]);

  const onLikeChange = (liked: boolean) => {
    setLocalIsLike(liked);
    setLocalLikeCount(prev => liked ? prev + 1 : Math.max(0, prev - 1));
    handleLike(liked);
  };

  // State for delete confirmation dialog
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const { openModal, closeModal } = useModal();


  // Check if current user is the owner of the feed
  const isOwner = auth.me?.activeAccount?.name === owner?.name;

  // Handle delete feed
  const handleDelete = async () => {
    if (!sid) return;

    try {
      setIsDeleting(true);
      await api.feed.delete(sid);
      // Close the dialog
      closeModal();
      // Call the onDelete callback if provided, instead of refreshing the page
      if (onDelete) {
        onDelete(sid);
      }
    } catch (error) {
      console.error('Error deleting feed:', error);
      toast.showToast('피드 삭제 중 오류가 발생했습니다.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle report feed
  const handleReport = async () => {
    if (!sid) return;

    try {
      setIsReporting(true);
      await api.feed.report(sid, '부적절한 콘텐츠');
      closeModal();
      toast.showToast('피드가 신고되었습니다.');
    } catch (error) {
      console.error('Error reporting feed:', error);
      toast.showToast('피드 신고 중 오류가 발생했습니다.');
    } finally {
      setIsReporting(false);
    }
  };

  // Handle block account
  const handleBlock = async () => {
    if (!owner?.name || !auth.me?.activeAccount?.name) return;

    try {
      setIsBlocking(true);
      await api.account.block(auth.me.activeAccount.name, owner.name);
      closeModal();

      // toast.showToast('계정이 차단되었어요. 내 계정 > 설정 > 계정 차단 메뉴에서 차단을 해제 할 수 있어요.');
      toast.showToast('계정이 차단되었어요');

      if (pathname?.startsWith('/feeds/')) {
        router.replace('/');
      } else if (onDelete) {
        onDelete(sid, owner.name);
      }
    } catch (error) {
      console.error('Error blocking account:', error);
      toast.showToast('계정 차단 중 오류가 발생했습니다.');
    } finally {
      setIsBlocking(false);
    }
  };

  // Open delete confirmation modal
  const openDeleteConfirmModal = () => {
    setShowMenu(false); // Close the menu
    openModal({
        title: '피드 삭제',
        content: <div>
          <Typography className={styles.modalContent} size="large" lineHeight={1.5}>
            정말로 이 피드를 삭제하시겠어요?<br />
            이 작업은 되돌릴 수 없어요.
          </Typography>

          <div className={styles.modalButtonContainer}>
            <Button
              variant="secondary"
              onClick={closeModal}
              disabled={isDeleting}
            >
              취소
            </Button>
            <Button
              className={styles.modalButtonDanger}
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? '삭제 중...' : '삭제'}
            </Button>
          </div>
        </div>,
      },
    );
  };

  // Open report confirmation modal
  const openReportConfirmModal = () => {
    setShowMenu(false); // Close the menu
    openModal({
        title: '피드 신고',
        content: <div>
          <Typography className={styles.modalContent} size="large" lineHeight={1.5}>
            이 피드를 부적절한 콘텐츠로 신고하시겠어요?
          </Typography>
          <div className={styles.modalButtonContainer}>
            <Button
              variant="secondary"
              onClick={closeModal}
              disabled={isReporting}
            >
              취소
            </Button>
            <Button
              onClick={handleReport}
              disabled={isReporting}
            >
              {isReporting ? '신고 중...' : '신고'}
            </Button>
          </div>
        </div>,

      },
    );
  };

  // Open block confirmation modal
  const openBlockConfirmModal = () => {
    setShowMenu(false); // Close the menu
    openModal({
        title: '계정 차단',
        content: <div>
          <Typography className={styles.modalContent} size="large" lineHeight={1.5}>
            정말로 이 계정을 차단하시겠어요?<br />
            차단 후에는 서로의 게시물을 볼 수 없어요.
          </Typography>
          <div className={styles.modalButtonContainer}>
            <Button
              variant="secondary"
              onClick={closeModal}
              disabled={isBlocking}
            >
              취소
            </Button>
            <Button
              className={styles.modalButtonDanger}
              onClick={handleBlock}
              disabled={isBlocking}
            >
              {isBlocking ? '차단 중...' : '차단'}
            </Button>
          </div>
        </div>,
      },
    );
  };

  // Ref for the component
  const feedRef = useRef<HTMLDivElement>(null);

  // State to track visibility
  const [isVisible, setIsVisible] = useState(false);

  // Timer ref
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Flag to track if div has been recorded
  const divRecordedRef = useRef(false);

  // Function to record div
  const recordView = useCallback(async () => {
    if (sid && !divRecordedRef.current) {
      try {
        await api.feed.view(sid);
        divRecordedRef.current = true;
      } catch (error) {
        console.error('Error recording div:', error);
        // Not showing toast for div recording errors as it's a background operation
      }
    }
  }, [sid, divRecordedRef]);

  // Use IntersectionObserver on web platforms
  useEffect(() => {
    // Get the DOM element from the ref
    const element = feedRef.current as unknown as HTMLElement;
    if (!element) return;

    // Create IntersectionObserver
    const observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        // We're only observing one element, so we can use the first entry
        const entry = entries[0];
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1, // Consider visible when at least 10% is visible
        root: null, // Use divport as root
      },
    );

    // Start observing the element
    observer.observe(element);

    // Cleanup function
    return () => {
      observer.disconnect();
    };
  }, []);

  // Effect to handle visibility and timer
  useEffect(() => {
    if (isVisible && !divRecordedRef.current) {
      // Start timer when component becomes visible
      timerRef.current = setTimeout(() => {
        recordView();
      }, 10000); // 10 seconds

    } else if (!isVisible && timerRef.current) {
      // Clear timer when component becomes invisible
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Cleanup on unmount
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isVisible, sid, recordView]);

  const handleClickMenu = () => {
    setShowMenu(!showMenu)
    if(!showMenu) {
      setTimeout(() => {
        const handleClickWindow = () => {
          setShowMenu(false);
          window.removeEventListener('click', handleClickWindow);
        };

        window.addEventListener('click', handleClickWindow);
      }, 50);
    }
  };

  return (
    <article
      className={`${styles.root} ${variant === 'list' ? styles.gallery : styles.detail}`}
      aria-label={`${ownerLabel}의 게시물`}
    >
      <CarouselProvider>
        {/* Profile section - moved between image and action buttons */}
        <div className={styles.profileSection}>
          <Link href={`/accounts/${owner?.name}`}>
            <div className={styles.profileLink}>
              <Avatar account={owner} size={32} variant="w96" />
              <div className={styles.profileInfo}>
                <Typography className={styles.profileName}>{owner?.nickname || owner?.name}</Typography>
                <Typography className={styles.profileUsername} size="small" weight="thin">@{owner?.name}</Typography>
              </div>
            </div>
          </Link>
          <div className={styles.profileActions}>
            {auth.me && auth.me.activeAccount?.name !== owner?.name ? (
              <span className={styles.followSlot}>
                <FollowButton
                  isFollowing={isFollowing}
                  isLoading={isLoading}
                  onChange={handleFollow}
                />
              </span>
            ) : ''}

            {/* More options button */}
            {auth.me && (
              <div
                className={`${styles.more} cursor-pointer`}
                onClick={handleClickMenu}
              >
                <MoreVertical
                  width={24}
                  height={24}
                  strokeWidth={1.5}
                />
                <div className={styles.dropdownContainer}>
                  {/* BottomSheet for feed options */}
                  <DropDown open={showMenu} stickyPosition="right">
                    {isOwner ?
                      <>
                        <DropDownItem className={styles.menuItemContainer}
                                      onSelect={() => router.push(`/feeds/${sid}/edit`)}>
                          <PenSquare width={20} height={20} strokeWidth={1.5} />
                          <Typography className={styles.text} size="small">피드 수정</Typography>
                        </DropDownItem>
                        <DropDownItem className={styles.menuItemContainer} onSelect={openDeleteConfirmModal}>
                          <Trash2 width={20} height={20} strokeWidth={1.5} color="#FF4D4F" />
                          <Typography className={`${styles.bottomSheetDangerText} ${styles.text}`} size="small">피드
                            삭제</Typography>
                        </DropDownItem>
                      </>
                      : <>
                        <DropDownItem className={styles.menuItemContainer} onSelect={openBlockConfirmModal}>
                          <UserMinus width={20} height={20} strokeWidth={1.5} />
                          <Typography>계정 차단</Typography>
                        </DropDownItem>
                        <DropDownItem className={styles.menuItemContainer} onSelect={openReportConfirmModal}>
                          <Flag width={20} height={20} strokeWidth={1.5} />
                          <Typography>피드 신고</Typography>
                        </DropDownItem>
                      </>
                    }
                  </DropDown>
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          ref={feedRef}
          className={styles.feedContainer}>
          {/* Feed images */}
          {files && files.length > 0 ? (
            <div
              className={`square-full ${styles.imageContainer}`}
              onClick={() => canNavigate && detailHref && router.push(detailHref)}
              style={canNavigate ? { cursor: 'pointer' } : undefined}
            >
              {files.length > 1 && (
                <span className={styles.multiImageBadge}>
                  <Layers width={16} height={16} strokeWidth={1.75} aria-hidden="true" />
                  <span className={styles.visuallyHidden}>사진 {files.length}장</span>
                </span>
              )}
              <Carousel>
                {files.map((fileItem, index) => (
                  <div key={index} className="square-full" style={{ aspectRatio: 1 }}>
                    <LNImage
                      className={styles.imageItem}
                      source={`${fileItem.file.url}`}
                      style={{ width: '100%', height: '100%', aspectRatio: 1 }}
                      width={480}
                      height={480}
                      variant="w2048"
                      sizes={variant === 'list'
                        ? '(max-width: 768px) 100vw, (max-width: 1279px) 50vw, 432px'
                        : '(max-width: 768px) 100vw, 960px'}
                      alt={feedImageAlt}
                      priority={isFirst && index === 0}
                    />
                  </div>
                ))}
              </Carousel>
            </div>
          ) : ''}

          {/* Image pagination indicators. PC 목록 카드는 높이를 사진·캡션으로만 정하므로 숨기고 장수 배지로 대신한다. */}
          <div className={styles.paginationSlot}>
            <ImagePagination
              size={files.length}
            />
          </div>

          <div className={styles.contentSection}>
            {/*{likes > 0 && <Typography className="font-bold mb-1">{likes}명이 좋아합니다</Typography>}*/}
            {caption && (
              <div
                className={styles.captionContainer}
                onClick={(event) => {
                  // 목록 카드는 본문을 눌러 상세에서 전문을 읽는다. 해시태그·멘션 링크는 그 링크로 이동한다.
                  if (variant !== 'list' || !canNavigate) return;
                  if ((event.target as HTMLElement).closest('a')) return;
                  if (detailHref) router.push(detailHref);
                }}
              >
                <Caption text={caption} />
              </div>
            )}

            {
              placeLinks.length > 0 && (
                <div className={styles.linksSection}>
                  <Typography className={styles.linksTitle}>연관 장소</Typography>
                  <div className={styles.linksContainer}>
                    {placeLinks.map((link, index) => (
                      <Link key={index} href={`/places/${link.place?.id}`}>
                        <div className={styles.linkItem}>
                          <Typography className={styles.linkText}>{link.place?.name}</Typography>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )
            }

            {props.buttons && props.buttons.length > 0 && (
              <div className={styles.buttonsSection}>
                {props.buttons
                  .filter((button) => {
                    const target = button.target ?? 'ALL';
                    if (target === 'ALL' || target === 'WEB') return true;
                    // 플랫폼을 판정하기 전(서버·첫 렌더)에는 플랫폼 전용 버튼을 그리지 않는다.
                    if (platform === 'UNKNOWN') return false;
                    if (target === 'WEB_PC') return platform === 'PC';
                    if (target === 'IOS_WEB') return platform === 'IOS';
                    if (target === 'ANDROID_WEB') return platform === 'ANDROID';
                    return false;
                  })
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((button) => (
                  <a
                    key={button.id}
                    href={button.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.feedButton} ${styles[button.style.toLowerCase()]}`}
                    onClick={() => {
                      if (sid) api.feedButton.click(sid, button.id).catch(console.error);
                    }}
                  >
                    {button.label}
                  </a>
                ))}
              </div>
            )}

            <div className={styles.contentFooter}>
              <Typography className={styles.timestamp}>
                {createdAt && (canNavigate && detailHref ? (
                  // 사진이 없는 글도 키보드로 상세에 갈 수 있도록 작성 시각을 상세 링크로 둔다.
                  <Link href={detailHref} className={styles.timestampLink} data-feed-link="">
                    <time dateTime={createdAt}>
                      {relativeTime ?? absoluteDateFormatter.format(new Date(createdAt))}
                    </time>
                    <span className={styles.visuallyHidden}> · 게시물 자세히 보기</span>
                  </Link>
                ) : (
                  <time dateTime={createdAt}>
                    {relativeTime ?? absoluteDateFormatter.format(new Date(createdAt))}
                  </time>
                ))}
              </Typography>
              <div className={styles.actionButtons}>
                <ShareButton
                  {...props}
                  className={`${styles.actionButtonGroup} ${styles.share}`}
                  onShare={() => setLocalShareCount(prev => prev + 1)}
                >
                  <Typography size="large" weight="normal">{simplifyNumber(localShareCount)}</Typography>
                </ShareButton>
                <button
                  type="button"
                  className={`${styles.actionButtonGroup} ${styles.comment}`}
                  onClick={onComment}
                  aria-label={`댓글 ${simplifyNumber(commentCount)}개 보기`}
                >
                  <MessageCircle
                    width={24}
                    height={24}
                    strokeWidth={1.5}
                    aria-hidden="true"
                  />
                  <Typography size="large" weight="normal">{simplifyNumber(commentCount)}</Typography>
                </button>
                <LikeButton
                  className={`${styles.actionButtonGroup} ${styles.like}`}
                  isLiked={localIsLike}
                  onChange={onLikeChange}
                >
                  <Typography size="large"
                              weight="normal">{simplifyNumber(localLikeCount)}</Typography>
                </LikeButton>
              </div>
            </div>
          </div>
        </div>
      </CarouselProvider>
    </article>
  );
};

export default FeedItem;
